import { SegmentedControl } from "@/components/ui/segmented-control";
import type { SegmentedControlOption } from "@/components/ui/segmented-control";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { GanttGranularity } from "@/domains/pm/utils/ganttScale";

/**
 * Day / week / month. Mirrors TimelineGranularityControl on the documents
 * timeline — the same control for the same job, so the two read alike.
 *
 * There is deliberately no continuous (ctrl-wheel) zoom: the Gantt lives
 * inside a vertically scrolling page, and hijacking the wheel there is a
 * usability trap. Three explicit steps also keep pxPerDay a known constant,
 * which is what lets the drag maths stay integral.
 */
export function GanttGranularityControl({
  value,
  onChange,
  tone = "neutral",
}: {
  value: GanttGranularity;
  onChange: (next: GanttGranularity) => void;
  /** The selected option's border: grey on the main chart, red in the
   *  snapshot comparison. */
  tone?: "neutral" | "brand";
}) {
  const { t } = useLanguage();
  const options: SegmentedControlOption<GanttGranularity>[] = [
    { value: "day", label: t.pm.issues.gantt.day },
    { value: "week", label: t.pm.issues.gantt.week },
    { value: "month", label: t.pm.issues.gantt.month },
  ];
  return (
    // Bare: on the Gantt only the chosen granularity is marked, by a border.
    <SegmentedControl
      variant="bare"
      tone={tone}
      value={value}
      onValueChange={(v: GanttGranularity) => onChange(v)}
      options={options}
    />
  );
}
